import React, {
  FC,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import generateUniqId from '../utilities/generateUniqId';

const TabIdSessionStorageKey = 'Roblox.TabId';

export type BrowserTabIdTrackingContext = { tabId?: string; isReady: boolean } | null;

const BrowserTabIdTrackingContext = createContext<BrowserTabIdTrackingContext>(null);

export const BrowserTabIdTrackingProvider: FC<React.PropsWithChildren> = ({ children }) => {
  const [tabId, setTabId] = useState<string>();
  const [isReady, setIsReady] = useState<boolean>(false);
  const onBeforeunload = useCallback(() => {
    // persist the same tab ID after redirect/refresh
    if (tabId) sessionStorage.setItem(TabIdSessionStorageKey, tabId);
  }, [tabId]);
  const setOrGetTabId = useCallback(() => {
    // generate initial tab ID or load the same tab ID after redirect/refresh
    const val = sessionStorage.getItem(TabIdSessionStorageKey);
    if (val) {
      setTabId(val);
      sessionStorage.removeItem(TabIdSessionStorageKey);
      // force new tab ID generation on duplicating tab
    } else {
      setTabId(generateUniqId());
    }
    setIsReady(true);
  }, []);
  useEffect(() => {
    if (!isReady) setOrGetTabId();
    window.addEventListener('unload beforeunload', onBeforeunload);
    return () => {
      window.removeEventListener('unload beforeunload', onBeforeunload);
    };
  }, [isReady, onBeforeunload, setOrGetTabId]);
  const value = useMemo(
    () => ({
      tabId,
      isReady,
    }),
    [tabId, isReady],
  );

  return (
    <BrowserTabIdTrackingContext.Provider value={value}>
      {children}
    </BrowserTabIdTrackingContext.Provider>
  );
};

/**
 *
 * Tracks the ID of the current browser tab, handling duplicate tab IDs
 * (done when user copies browser tab to create new one)
 * ref: https://stackoverflow.com/a/60164111
 *
 * @returns
 *    tabId: string   unique browser tab ID
 */

export function useBrowserTabIdTracking() {
  const context = useContext(BrowserTabIdTrackingContext);
  if (!context) {
    throw new Error(`useBrowserTabIdTracking must be used within a BrowserTabIdTrackingProvider`);
  }
  return context;
}

export default BrowserTabIdTrackingProvider;
